import { isEmpty, each } from 'lodash';
import { change, reset } from 'redux-form';
import { pipe } from 'fp-ts/function';
import { string } from 'fp-ts';
import { AsyncAction, CommanderThunkAction } from '@mews-commander/types';
import { createAction, lt, showSuccessAlert } from '@mews/framework';
import { Address } from '@mews-commander/data-access';
import { createUpdateValue } from '@mews-commander/utils';
import { ADDRESS_UPDATE_FAILED, getAddressTitle } from '@mews-commander/legacy-entities/src/ducks/addresses';
import {
    addAddress as addAddressApi,
    updateAddress as updateAddressApi,
    deleteAddress as deleteAddressApi,
} from '@mews-commander/legacy-entities/src/actions/addresses';
import { getCountrySubdivisionByCode } from '@mews-commander/legacy-entities/src/ducks/countrySubdivisions';
import { createAddAction, createEditAction, createDeleteAction } from '../entityManagement';
import { changePaginationIds } from '../pagination/actions';
import { getOwnerSortedAddressIds, getSelectedAddress } from './selectors';
import { AddressFormValues } from './types';

const changeAddressesList =
    (ownerId: string, scope: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const ids = getOwnerSortedAddressIds(getState(), { ownerId });
        dispatch(changePaginationIds(ids, scope));
    };

export function autocompleteAddress(form: string, values: AddressFormValues): CommanderThunkAction {
    return (dispatch, getState) => {
        dispatch(reset(form));
        each(values, (value, field) => {
            if (
                field === `countrySubdivisionCode` &&
                isEmpty(getCountrySubdivisionByCode(getState(), { countrySubdivisionCode: value }))
            ) {
                return true;
            }

            dispatch(change(form, field, value));
        });
    };
}

export const clearCountrySubdivision = (form: string) => change(form, 'countrySubdivisionCode', null);

const addressAdder = createAddAction(addAddressApi);
export const addAddress =
    (
        values: AddressFormValues,
        ownerId: string,
        scope: string,
        isCompany: boolean
    ): CommanderThunkAction<AsyncAction> =>
    (dispatch) =>
        dispatch(addressAdder({ ...trimAddressLines(values), ownerId, isCompany }, scope)).then(() => {
            dispatch(changeAddressesList(ownerId, scope));
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        });

const addressEditer = createEditAction(updateAddressApi);
export const editAddress =
    (values: AddressFormValues, ownerId: string, scope: string): CommanderThunkAction<AsyncAction> =>
    (dispatch, getState) => {
        const current = getSelectedAddress(getState(), { ownerId, scope });

        if (!current) {
            dispatch(createAction(ADDRESS_UPDATE_FAILED));
            return;
        }

        const addressUpdates = [
            {
                id: current.id,
                addressLine1: createUpdateValue(
                    current.addressLine1,
                    values.addressLine1 ? pipe(values?.addressLine1, string.trim) : null
                ),
                addressLine2: createUpdateValue(
                    current.addressLine2,
                    values.addressLine2 ? pipe(values?.addressLine2, string.trim) : null
                ),
                city: createUpdateValue(current.city, values.city),
                countryCode: createUpdateValue(current.countryCode, values.countryCode),
                countrySubdivisionCode: createUpdateValue(
                    current.countrySubdivisionCode,
                    values.countrySubdivisionCode
                ),
                postalCode: createUpdateValue(current.postalCode, values.postalCode),
            },
        ];

        return dispatch(addressEditer({ addressUpdates, ownerId }, scope)).then(() => {
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        });
    };

const addressDeleter = createDeleteAction(deleteAddressApi);
export const deleteAddress =
    (ownerId: string, scope: string): CommanderThunkAction<AsyncAction> =>
    (dispatch) =>
        dispatch(addressDeleter({ ownerId }, scope)).then((deletedAddress: Address) => {
            const addressTitle = getAddressTitle(deletedAddress);
            dispatch(changeAddressesList(ownerId, scope));
            showSuccessAlert({
                // @ts-expect-error $TSFixMe Type 'null' is not assignable to type 'string'
                description: lt('DeleteSuccessMessage', { entity: addressTitle }),
            });
        });

const trimAddressLines = (values: AddressFormValues) => {
    const { addressLine1, addressLine2 } = values;
    const cleanAddressLine1 = addressLine1 ? pipe(addressLine1, string.trim) : null;
    const cleanAddressLine2 = addressLine2 ? pipe(addressLine2, string.trim) : null;
    return { ...values, addressLine1: cleanAddressLine1, addressLine2: cleanAddressLine2 };
};
